import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { NO_PROFILE_FILTERS, type CompanyProfileFilterValues } from '../profileFilters';

/**
 * Persisted view-mode preference for the Companies list (CLAUDE.md §2.3).
 *
 * Search lives in URL-bound page state and stays out of the store — that's a
 * server-driven query param, not a durable user pref.
 */
export type CompaniesViewMode = 'table' | 'list' | 'cards' | 'grouped';

export const COMPANIES_VIEW_MODES: CompaniesViewMode[] = [
  'table',
  'list',
  'cards',
  'grouped',
];

interface CompaniesUiState {
  view: CompaniesViewMode;
  setView: (view: CompaniesViewMode) => void;
  /** Transient toggle — refreshes default to the active list. */
  showArchived: boolean;
  setShowArchived: (showArchived: boolean) => void;
  /** Current DRF `OrderingFilter` value (e.g. `-created`). Session-only. */
  ordering: string | undefined;
  setOrdering: (ordering: string | undefined) => void;
  /** Persisted tag filter — survives reloads. */
  tag: string;
  setTag: (tag: string) => void;
  /** Persisted industry / region / technology filters (ADR-108). */
  profile: CompanyProfileFilterValues;
  setProfile: (profile: CompanyProfileFilterValues) => void;
}

export const useCompaniesUi = create<CompaniesUiState>()(
  persist(
    (set) => ({
      view: 'table',
      setView: (view) => set({ view }),
      showArchived: false,
      setShowArchived: (showArchived) => set({ showArchived }),
      ordering: undefined,
      setOrdering: (ordering) => set({ ordering }),
      tag: 'all',
      setTag: (tag) => set({ tag }),
      profile: NO_PROFILE_FILTERS,
      setProfile: (profile) => set({ profile }),
    }),
    {
      name: 'crm.companies:ui',
      // View mode + tag filter persist; the archived toggle and ordering stay
      // ephemeral so refreshes return to a clean default.
      partialize: (state) => ({ view: state.view, tag: state.tag, profile: state.profile }),
    },
  ),
);
